/*=========================================================================================
  File Name: module-usertax-actions.js
  Description: UserTax Module Actions
  ----------------------------------------------------------------------------------------
  Project: MISA MTAX WebApp
  Author: MISA JSC
  Author URL: http://www.misa.com.vn
==========================================================================================*/
import PayTaxPaymentAPI from '@/api/PayTaxPaymentAPI';
import PayFileAPI from '@/api/PayFileAPI';
import ServiceRegisBankAPI from '@/api/ServiceRegisBankAPI';
import httpClient from '../../api/axios-httpclient';
import commonFunc from '@/common/commonFunction';
import i18n from '@/i18n/i18n';
import EventBusGlobal, { GlobalEventName } from '@/sys/eventBusGlobal';

/**
 * Xử lý lỗi khi xuất khẩu - chỉ hiển thị 1 thông báo lỗi cho khách hàng
 * @param {Error|Object} error - Đối tượng lỗi
 * @param {String} exportKey - Key của export task (optional)
 * @returns {Object} Object chứa thông tin lỗi
 */
const handleExportError = (error, exportKey) => {
	// Thông báo lỗi đơn giản cho khách hàng
	const errorMessage =
		'Đã có lỗi xảy ra khi xuất dữ liệu. Vui lòng thử lại sau';

	// Hiển thị thông báo lỗi
	commonFunc.toast({
		type: 'error',
		text: errorMessage,
		position: 'top-center'
	});

	// Xóa download task khỏi danh sách nếu có
	if (exportKey) {
		EventBusGlobal.$emit(GlobalEventName.removeDownloadTask, exportKey);
	}

	return { success: false, error: errorMessage };
};

/**
 * Đợi nhận SignalR notification với timeout
 * Helper function để đơn giản hóa logic xử lý SignalR
 *
 * Chi tiết hoạt động:
 * 1. Đăng ký listener để lắng nghe export notification từ SignalR hub
 * 2. Nếu nhận được notification với đúng exportKey → trả về ngay với data
 * 3. Nếu timeout mà chưa nhận được → trả về flag để fallback sang polling
 * 4. Tự động cleanup listener để tránh memory leak
 *
 * @param {String} exportKey - Key của export task cần theo dõi
 * @param {Number} timeoutSeconds - Thời gian chờ tối đa (giây)
 * @returns {Promise<Object>}
 *   - { fromSignalR: true, notification: Object } nếu nhận được SignalR
 *   - { fromSignalR: false } nếu timeout, cần fallback sang polling
 */
const waitForSignalR = (exportKey, timeoutSeconds) => {
	// Biến cờ để track xem đã nhận được SignalR notification chưa
	// Dùng để tránh race condition giữa timeout và notification
	let signalRReceived = false;

	return new Promise(resolve => {
		// Handler function: xử lý khi nhận được notification từ SignalR
		// Sẽ được gọi mỗi khi có export notification được broadcast
		const handler = notification => {
			// Kiểm tra notification có đúng export key mà ta đang chờ không
			// (Vì có thể có nhiều export đang chạy đồng thời)
			if (notification.key === exportKey) {
				// Đánh dấu đã nhận được SignalR để timeout handler bi��t
				signalRReceived = true;

				// Hủy đăng ký listener ngay để tránh:
				// - Memory leak (listener không được cleanup)
				// - Duplicate handling (xử lý cùng 1 notification nhiều lần)
				EventBusGlobal.$off(
					GlobalEventName.exportNotification,
					handler
				);

				// Trả về kết quả thành công với notification data
				// Component gọi sẽ biết đã nhận được SignalR và không cần polling
				resolve({ fromSignalR: true, notification });
			}
			// Nếu không đúng key, bỏ qua và chờ notification tiếp theo
		};

		// Đăng ký listener lên EventBus để lắng nghe export notification
		// Listener sẽ được trigger mỗi khi SignalR hub broadcast notification
		EventBusGlobal.$on(GlobalEventName.exportNotification, handler);

		// Setup timeout: nếu quá thời gian mà chưa nhận được SignalR
		// thì sẽ fallback sang cơ chế polling
		setTimeout(() => {
			// Kiểm tra xem đã nhận được SignalR chưa
			if (!signalRReceived) {
				// Chưa nhận được → cleanup listener
				EventBusGlobal.$off(
					GlobalEventName.exportNotification,
					handler
				);

				// Trả về flag fromSignalR = false
				// Component gọi sẽ biết cần fallback sang polling
				resolve({ fromSignalR: false });
			}
			// Nếu đã nhận được SignalR thì không làm gì (handler đã resolve rồi)
		}, timeoutSeconds * 1000);
	});
};

export default {
	loadTaxPaper: async function(context, payload) {
		//Gán cờ loading
		context.commit('updatedTaxPaperLoaded', false);

		//Gọi dịch vụ
		let res = await httpClient.postAsync({
			url: PayTaxPaymentAPI.getAPIUrl() + '/paging',
			data: payload
		});

		if (res && res.Success) {
			context.commit('updateTaxPaperList', res);
		}

		//Gán cờ loading
		context.commit('updatedTaxPaperLoaded', true);

		// Trả kết quả
		return res;
	},

	loadMustPayTaxAmountReportData: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url:
					PayTaxPaymentAPI.getAPIUrl() + '/getmustpaytaxamountreport',
				data: payload
			},
			true
		);
		if (res && res.Success) {
			context.commit('updateMustPayTaxAmountReportData', res.Data);
		} else {
			context.commit('updateMustPayTaxAmountReportData', []);
		}
		// Trả kết quả
		return res;
	},

	loadMustPayTaxAmountReportDetailData: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url:
					PayTaxPaymentAPI.getAPIUrl() +
					'/getmustpaytaxamountreportdetail',
				data: payload
			},
			true
		);
		if (res && res.Success) {
			context.commit('updateMustPayTaxAmountReportDetailData', res.Data);
		} else {
			context.commit('updateMustPayTaxAmountReportDetailData', null);
		}
		// Trả kết quả
		return res;
	},

	loadPayerBank: async function(context, payload) {
		let res = await httpClient.postAsync({
			url: ServiceRegisBankAPI.getAPIUrl() + '/getbank',
			data: payload
		});

		if (res && res.Success) {
			context.commit('updatePayerBankList', res);
			return res.Data;
		}

		return [];
	},
	loadBankByTaxPaper: async function(context, payload) {
		let res = await httpClient.getAsync({
			url: ServiceRegisBankAPI.getAPIUrl() + '/getbankbytaxpaper',
			data: payload
		});

		if (res && res.Success) {
			context.commit('updateBankByTaxPaper', res.Data);
			return res.Data;
		}

		return null;
	},
	deleteTaxPaper: async function(context, options) {
		let res = await httpClient.postAsync(
			{
				url: PayTaxPaymentAPI.getAPIUrl() + '/delete',
				data: options.payload
			},
			true
		);

		if (res && res.Success) {
			commonFunc.toast({
				type: 'success',
				text: i18n.t('i18nTaxPaper.List.DeleteSuccess'),
				position: 'top-center'
			});

			context.commit('deleteTaxPaperList', options.item);
		}

		// Trả kết quả
		return res;
	},
	// thực hiện lấy thông tin bản ghi GNT
	loadPayTaxPaymentSubmission: async function(context, payload) {
		let res = await httpClient.getAsync({
			url: PayTaxPaymentAPI.getAPIUrl() + '/get',
			params: payload
		});

		if (res && res.Success) {
			context.commit('updatePayTaxPaymentSubmission', res);
			return res.Data;
		}

		return {};
	},
	// thực hiện lấy thông tin bản ghi GNT được đặt làm mặc định
	loadPayTaxPaymentSubmissionDefault: async function(context, payload) {
		let res = await httpClient.getAsync({
			url: PayTaxPaymentAPI.getAPIUrl() + '/getdefault',
			params: payload
		});

		if (res && res.Success) {
			context.commit('updatePayTaxPaymentSubmissionDefault', res);
			return res.Data;
		}

		return {};
	},
	// mang thông tin đi tạo file GNT
	createXml: async function(context, payload) {
		let res = await httpClient.postAsync({
			url: PayTaxPaymentAPI.getAPIUrl() + '/createxml',
			data: payload
		});

		if (res && res.Success) {
			context.commit('updateFileData', res);
		}

		// Trả kết quả
		return res;
	},
	// thực hiện gửi thông tin đăng ký sang queue để xử lý
	registration: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: PayTaxPaymentAPI.getAPIUrl() + '/registration',
				data: payload
			},
			true
		);

		if (res && res.Success) {
			context.commit('regisSuccess', res);
		}

		// Trả kết quả
		return res.Success;
	},
	// thực hiện lấy thông tin số thuế phải nộp
	getGIPInfo: async function(context, payload) {
		let res = await httpClient.getAsync(
			{
				url: PayTaxPaymentAPI.getAPIUrl() + '/gip',
				params: payload
			},
			true
		);

		if (res && res.Success) {
			context.commit('updateGIPInfo', res);
			return res.Data;
		}

		return {};
	},
	// thực hiện lấy thông tin người thụ hưởng
	getInfoTax: async function(context, payload) {
		let res = await httpClient.getAsync(
			{
				url: PayTaxPaymentAPI.getAPIUrl() + '/gipInfo',
				params: payload
			},
			true
		);

		if (res && res.Success) {
			return res.Data;
		}

		return {};
	},
	// thực hiện lấy thông tin cached của bên tích hợp
	getCachedTaxpaperIntegration: async function(context, payload) {
		let res = await httpClient.getAsync(
			{
				url:
					PayTaxPaymentAPI.getAPIUrl() +
					'/getcachedtaxpaperintegration',
				params: payload
			},
			true
		);

		if (res && res.Success) {
			return res.Data;
		}

		return {};
	},
	exportMustPayTaxAmountReport: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url:
					PayTaxPaymentAPI.getAPIUrl() +
					'/getmustpaytaxamountreportdata',
				data: payload
			},
			true
		);

		if (res && res.Success && res.Data) {
			return (
				PayFileAPI.getAPIUrl() +
				'/exportgetmustpaytaxamountreport?key=' +
				res.Data
			);
		}
		return null;
	},
	/**
	 * Tra cứu thông tin nợ thuế từ cơ quan thuế
	 * @param {Object} context - Vuex context
	 * @param {Object} payload - Tham số truy vấn
	 * @param {String} payload.MST - Mã số thuế cần tra cứu
	 * @param {String} payload.LoaiDL - Loại dữ liệu tra cứu (VD: '00' - tra cứu tất cả)
	 * @returns {Promise<Object|null>} Trả về object chứa thông tin nợ thuế hoặc null nếu có lỗi
	 */
	searchTaxDebt: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: PayTaxPaymentAPI.getAPIUrl() + '/search-tax-debt',
				data: payload
			},
			true
		);

		if (res && res.Success) {
			context.commit('updateGIPInfo', res);
			return res.Data;
		}

		return null;
	},
	/**
	 * Kiểm tra danh sách DecNo có trùng với các khoản nộp đã có trong hệ thống hay không
	 * @param {*} context - Vuex context
	 * @param {Object} payload - Payload chứa thông tin kiểm tra
	 * @param {Array<string>} payload.PaymentItemIds - Danh sách ID khoản nộp cần kiểm tra
	 * @param {Array<number>} payload.Statuses - Danh sách trạng thái cần kiểm tra (VD: [0] cho Draft, [0,1,2] cho nhiều trạng thái)
	 * @returns {Promise<Array>} - Danh sách các PayTaxPaymentSubmission có chứa DecNo trùng (chỉ trả về SubmissionId)
	 */
	checkDuplicateDecNos: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url: PayTaxPaymentAPI.getAPIUrl() + '/check-duplicate-decnos',
				data: payload
			},
			true
		);

		if (res && res.Success) {
			return res.Data;
		}

		return [];
	},
	/**
	 * Kiểm tra thứ tự thanh toán của các khoản nộp
	 * @param {*} context - Vuex context
	 * @param {Object} payload - Payload chứa thông tin kiểm tra
	 * @param {string} payload.TaxCode - Mã số thuế
	 * @param {Array<Object>} payload.PaymentItems - Danh sách khoản nộp cần kiểm tra
	 * @param {string} payload.PaymentItems[].PaymentItemId - ID khoản nộp
	 * @param {number} payload.PaymentItems[].RequiredAmount - Số tiền yêu cầu
	 * @returns {Promise<boolean|null>} - true nếu vi phạm thứ tự, false nếu hợp lệ, null nếu có lỗi
	 */
	checkSequentialOrderPayment: async function(context, payload) {
		let res = await httpClient.postAsync(
			{
				url:
					PayTaxPaymentAPI.getAPIUrl() +
					'/check-sequential-order-payment',
				data: payload
			},
			true
		);

		if (res && res.Success) {
			return res.Data;
		}

		return null;
	},
	previewTaxPaper: async function(context, payload) {
		let res = await PayTaxPaymentAPI.postAsync(
			{
				url: PayTaxPaymentAPI.getAPIUrl() + '/preview',
				data: payload
			},
			true
		);

		if (res && res.Success) {
			return res;
		}

		return null;
	},
	saveDraftTaxPaper: async function(context, payload) {
		return await PayTaxPaymentAPI.postAsync(
			{
				url: PayTaxPaymentAPI.getAPIUrl() + '/draft',
				data: payload
			},
			true
		);
	},

	/**
	 * Kiểm tra trạng thái export từ API status endpoint
	 * @param {Object} context - Vuex context
	 * @param {String} exportKey - Export key để check status
	 * @returns {Promise<Object|null>} Status response hoặc null nếu lỗi
	 */
	checkExportStatus: async function(context, exportKey) {
		try {
			const response = await httpClient.getAsync(
				{
					url: PayFileAPI.getAPIUrl() + `/export-status/${exportKey}`,
					params: {}
				},
				false
			);
			if (response && response.Success) {
				return response.Data;
			}

			// Response format: { status: 0|1|2, message: string, fileName: string, taxCode: string }
			return null;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Download file đã export hoàn thành từ server
	 * @param {Object} context - Vuex context
	 * @param {Object} payload - Tham số download
	 * @param {String} payload.key - Export key để download file
	 * @param {String} payload.fileName - Tên file để download
	 * @returns {Promise<Object>} Kết quả download
	 */
	downloadFile: async function(context, payload) {
		try {
			// Gọi API download file với responseType: 'arraybuffer'
			// Backend sẽ trả về:
			// - HTTP 200 + ArrayBuffer nếu thành công
			// - HTTP 400/404 + ServiceResult JSON nếu lỗi (Axios tự động parse JSON)
			const response = await httpClient.getAsync(
				{
					url: PayFileAPI.getAPIUrl() + '/downloadcompletedexport',
					params: { key: payload.key },
					responseType: 'arraybuffer'
				},
				false
			);

			// Kiểm tra response type để phân biệt success vs error
			if (response instanceof ArrayBuffer) {
				// Trường hợp thành công: response là ArrayBuffer (file binary)
				commonFunc.downloadByBlob(
					response,
					'application/vnd.ms-excel',
					payload.fileName
				);

				// Hiển thị thông báo thành công
				commonFunc.toast({
					type: 'success',
					text: 'Tải file thành công',
					position: 'top-center'
				});

				return true;
			}

			// Trường hợp không xác định (fallback)
			commonFunc.toast({
				type: 'error',
				text: 'Đã có lỗi xảy ra khi tải file. Vui lòng thử lại sau',
				position: 'top-center'
			});

			return false;
		} catch (error) {
			let errorMessage = 'Đã có lỗi xảy ra khi tải file. Vui lòng thử lại sau';
			// Hiển thị thông báo lỗi
			commonFunc.toast({
				type: 'error',
				text: errorMessage,
				position: 'top-center'
			});

			return false;
		}
	},

	/**
	 * Xuất danh sách giấy nộp tiền
	 * Flow: Gọi API tạo key → Emit event add task → Đợi SignalR (15s) → Fallback polling
	 * @param {Object} context - Vuex context
	 * @param {Object} payload - Tham số xuất khẩu
	 * @returns {Promise<Object>} Kết quả export
	 */
	exportTaxPaperList: async function(context, payload) {
		let exportKey = null;

		try {
			// Gọi API để khởi tạo quá trình export
			const response = await httpClient.postAsync(
				{
					url: PayTaxPaymentAPI.getAPIUrl() + '/gettaxpaperlistdata',
					data: payload
				},
				true
			);

			// Kiểm tra response
			if (!response || !response.Success) {
				return null;
			}

			exportKey = response.Data;
			const fileName = `${
				payload && payload.RequestParams
					? `${payload.RequestParams.TaxCode}_`
					: ''
			}GNT.xlsx`;

			// Emit event để thêm download task
			EventBusGlobal.$emit(GlobalEventName.addDownloadTask, {
				key: exportKey,
				fileName: fileName
			});

			// Đợi SignalR notification với timeout (sử dụng helper function)
			const signalRTimeout =
				window.__env.siteConfig.export_polling_interval || 30;
			const result = await waitForSignalR(exportKey, signalRTimeout);

			if (result.fromSignalR) {
				// Nhận được SignalR notification
				return { success: true, fromSignalR: true };
			} else {
				// Timeout - fallback sang polling
				return await context.dispatch('pollExportStatus', {
					exportKey,
					fileName
				});
			}
		} catch (error) {
			// Xử lý lỗi và hiển thị thông báo
			return handleExportError(error, exportKey);
		}
	},

	/**
	 * Polling fallback - kiểm tra trạng thái export định kỳ
	 * Sử dụng checkExportStatus để kiểm tra trạng thái
	 * @param {Object} context - Vuex context
	 * @param {Object} payload - Tham số polling
	 * @param {String} payload.exportKey - Key của export task
	 * @param {String} payload.fileName - Tên file export
	 * @returns {Promise<Object>} Kết quả polling
	 */
	pollExportStatus: async function(context, payload) {
		const { exportKey, fileName } = payload;
		const pollingInterval =
			window.__env.siteConfig.export_polling_interval || 15; // seconds
		const maxAttempts =
			window.__env.siteConfig.export_polling_max_attempts || 4;
		let attempts = 0;

		// Lấy ExportStatusEnum từ global
		const MSEnum = (window.$ms && window.$ms.enum) || {};
		const ExportStatusEnum = MSEnum.ExportStatusEnum || {
			Processing: 0,
			Completed: 1,
			Error: 2,
			NotExistsFile: 3
		};

		while (attempts < maxAttempts) {
			attempts++;

			try {
				// Đợi interval trước khi poll
				await new Promise(resolve =>
					setTimeout(resolve, pollingInterval * 1000)
				);

				// Sử dụng checkExportStatus action để kiểm tra trạng thái
				let statusData = await context.dispatch(
					'checkExportStatus',
					exportKey
				);
				if (!statusData) {
					// API lỗi - tiếp tục polling
					continue;
				}

				// Xử lý các trạng thái export
				if (statusData.Status === ExportStatusEnum.Completed) {
					// Export hoàn thành - emit event để DownloadProgressManager xử lý download
					EventBusGlobal.$emit(GlobalEventName.exportNotification, {
						type: 'completed',
						key: exportKey,
						fileName: statusData.fileName || fileName,
						message: statusData.message || 'Xuất khẩu thành công'
					});

					return { success: true, fromPolling: true };
				} else if (
					statusData.Status === ExportStatusEnum.NotExistsFile
				) {
					// File không tồn tại - DỪNG NGAY không retry
					EventBusGlobal.$emit(GlobalEventName.exportNotification, {
						type: 'error',
						key: exportKey,
						fileName: fileName,
						message: statusData.message || 'File không tồn tại'
					});

					// Hiển thị thông báo lỗi
					commonFunc.toast({
						type: 'error',
						text:
							statusData.message ||
							'File không tồn tại. Vui lòng thử lại sau',
						position: 'top-center'
					});

					EventBusGlobal.$emit(
						GlobalEventName.removeDownloadTask,
						exportKey
					);

					// RETURN NGAY - không retry
					return { success: false, error: 'NotExistsFile' };
				} else if (statusData.Status === ExportStatusEnum.Error) {
					// Export lỗi - emit error event và DỪNG
					EventBusGlobal.$emit(GlobalEventName.exportNotification, {
						type: 'error',
						key: exportKey,
						fileName: fileName,
						message: statusData.message || 'Lỗi khi xuất khẩu'
					});

					// Hiển thị thông báo lỗi
					commonFunc.toast({
						type: 'error',
						text:
							'Đã có lỗi xảy ra khi xuất dữ liệu. Vui lòng thử lại sau',
						position: 'top-center'
					});

					EventBusGlobal.$emit(
						GlobalEventName.removeDownloadTask,
						exportKey
					);

					return { success: false, error: 'Export failed' };
				}
				// else: status === Processing (0), tiếp tục polling
			} catch (error) {
				// Xử lý lỗi và hiển thị thông báo
				commonFunc.toast({
					type: 'error',
					text:
						'Đã có lỗi xảy ra khi xuất dữ liệu. Vui lòng thử lại sau',
					position: 'top-center'
				});

				EventBusGlobal.$emit(
					GlobalEventName.removeDownloadTask,
					exportKey
				);

				return { success: false, error: error.message };
			}
		}

		// Vượt quá số lần thử - emit error event
		EventBusGlobal.$emit(GlobalEventName.exportNotification, {
			type: 'error',
			key: exportKey,
			fileName: fileName,
			message: 'Quá thời gian chờ xuất khẩu'
		});

		// Hiển thị thông báo lỗi
		commonFunc.toast({
			type: 'error',
			text: 'Đã có lỗi xảy ra khi xuất dữ liệu. Vui lòng thử lại sau',
			position: 'top-center'
		});

		EventBusGlobal.$emit(GlobalEventName.removeDownloadTask, exportKey);

		return { success: false, error: 'Timeout' };
	}
};
